import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, viewChild } from '@angular/core';
import { AppUtils } from 'src/helpers';
import { NzDrawerComponent } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DisplayListUserMediaAppModel, FilterModel, ListItemsFilterModel, MediaAppListItemModel, MediaListItemModel, UserMediaAppEditModel, UserMediaAppSummaryModel } from 'src/app/shared/models';
import { BaseService, DisplayListService, MediaAppService, UserMediaAppService } from 'src/app/shared/services';
import { toNumber } from 'ng-zorro-antd/core/util';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { addDarkToImageName } from 'src/app/shared/services/functions';
import { SharedModule } from '../../../../shared/shared.module';
import { MediaStoreComponent } from '../../media-app/store/media-store.component';
import { DisplayLivePreviewComponent } from 'src/app/display/components/live-preview/display-live-preview.component';

@Component({
    standalone: true,
    imports: [SharedModule, MediaStoreComponent],
    selector: 'app-media-list-multi-select',
    templateUrl: './media-list-multi-select.component.html',
    styleUrls: ['./media-list-multi-select.component.scss']
})
export class MediaListMultiSelectComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild('drawer') drawer: NzDrawerComponent;
    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;
    @Input() medias: Array<UserMediaAppSummaryModel> = new Array<UserMediaAppSummaryModel>();
    @Input() selectedMediaList: Array<UserMediaAppSummaryModel> = new Array<UserMediaAppSummaryModel>();
    @Input() selectedMediaIds: Array<number> = new Array<number>();
    @Input() addMediaToPlaylist: boolean;
    @Input() selectedPlaylistId: number;
    @Input() displayListId: number;
    @Input() groupId: number;
    @Input() singleSelection: boolean = false;
    @Input() labelText: string = 'Send to Playlist';
    @Input() allowedMediaAppTypes: string[] | null = null;
    @Output() closeDrawer: EventEmitter<{ selectedMedia: Array<UserMediaAppSummaryModel>, updateValue: boolean }> = new EventEmitter<{ selectedMedia: Array<UserMediaAppSummaryModel>, updateValue: boolean }>();

    isLoading = false;
    isModelLoaded = false;
    model: Array<UserMediaAppSummaryModel>;
    filterKey: string;
    sortOrder: string;
    allMediaSelected = false;
    isMediaSelected: boolean;
    hasMediaList: boolean = true;
    direction: string;
    isDrawerVisible = false;
    showMediaDrawer = false;
    newMediaId = 0;
    newMedia = new Array<MediaListItemModel>();
    newMediaIds = new Array<number>();
    hasMore = true;
    loadingMore: boolean;
    totalRecords: number;
    recordsToSkip: number;
    pageSize: number;
    filterModel = new FilterModel();
    subscriptions = new Array<Subscription>();
    mediaApps = new Array<MediaAppListItemModel>();

    /** Debounced search subject — fires getMedia() 300 ms after the user stops typing */
    private readonly searchInput$ = new Subject<string>();

    constructor(
        private service: UserMediaAppService,
        private baseService: BaseService,
        private displayListService: DisplayListService,
        public appUtils: AppUtils,
        private mediaAppService: MediaAppService,
        private modal: NzModalService
    ) {
        this.model = new Array<UserMediaAppSummaryModel>();
        this.filterModel.sortBy = 'Title';
        this.filterModel.sortDir = 'asc';
    }

    ngOnChanges(changes: SimpleChanges): void { }

    ngOnInit() {
        // Wire debounced search: triggers getMedia() 300 ms after each keystroke
        const searchSub = this.searchInput$.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(() => this.getMedia());
        this.subscriptions.push(searchSub);

        this.getMediaAppList();
    }
 getMediaAppList(): void {
        const requestSubscription =
            this.mediaAppService.getList()
                .subscribe({
                    next: (response: any) => {
                        this.mediaApps = new Array<MediaAppListItemModel>();
                        response.forEach(x => {
                            x.iconUrl = x.iconUrl ? x.iconUrl : this.generateMediaIconUrl(x.typeString);
                            x.iconUrlDark = addDarkToImageName(x.iconUrl);
                            this.mediaApps.push(x);
                        });

                        if (this.allowedMediaAppTypes && this.allowedMediaAppTypes.length > 0) {
                            this.mediaApps = this.mediaApps.filter(app =>
                                this.allowedMediaAppTypes.includes((app as any).typeString)
                            );
                        }
                        
                        this.isLoading = false;
                        this.getMedia(true);
                    },
                    error: (error) => {
                        this.isLoading = false;
                        this.baseService.processErrorResponse(error);
                    }
                });

        this.subscriptions.push(requestSubscription);
    }
    ngAfterViewInit() {
        this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
    }

    onScroll(event: any) {
        const element = this.scrollContainer.nativeElement;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        if (scrollTop + clientHeight >= scrollHeight * 0.8 && this.hasMore && !this.loadingMore) {
            this.getMoreRecords();
        }
    }

    getMedia(initialLoading = false, limit = 15) {
        this.isLoading = true;
        this.isModelLoaded = false;
        this.filterModel.offset = 0;
        this.filterModel.limit = limit;
        this.filterModel.mineOnly = false;
        this.filterModel.mediaAppIds = this.allowedMediaAppTypes;
        this.totalRecords = 0;
        const requestSubscription =
            this.service.getList(this.groupId, this.filterModel).subscribe({
                next: (response: any) => {
                    this.model = response;
                    this.totalRecords = response.length;
                    if (this.model.length > 0) {
                        this.model.forEach((x) => {
                            const mediaApp = this.mediaApps.find(row => row.id == x.mediaAppId);
                            x.iconUrl = mediaApp && mediaApp.iconUrl ? mediaApp.iconUrl : this.generateMediaIconUrl(x.mediaAppType);
                            x.iconUrlDark = mediaApp && mediaApp.iconUrlDark ? mediaApp.iconUrlDark : addDarkToImageName(x.iconUrl);
                            x.showDefaultIcon = true;
                            x.lengthInSeconds = x.lengthInSeconds == 0 ? 10 : x.lengthInSeconds;
                            x['isPaidAddOn'] = (mediaApp as any)?.isPaidAddOn;
                        });
                    }

                    if (initialLoading) {
                        this.hasMediaList = this.model.length > 0;
                    }

                    const filteredElements = this.model.filter(x => this.newMediaIds.includes(x.id));
                    if (filteredElements && filteredElements.length > 0){
                        this.model = this.model.filter(x => !this.newMediaIds.includes(x.id));
                    }

                    if (this.newMedia && this.newMedia.length > 0) {
                        this.newMedia.forEach((x) => {
                            let userAppMedia = new UserMediaAppSummaryModel();
                            userAppMedia.id = x.id;
                            userAppMedia.groupId = this.groupId;
                            userAppMedia.mediaAppTitle = x.mediaAppTitle;
                            userAppMedia.mediaAppType = x.mediaAppType;
                            userAppMedia.status = x.status;
                            userAppMedia.title = x.title;
                            userAppMedia.iconUrl = x.iconUrl;
                            userAppMedia.videoEncodingStatus = x.videoEncodingStatus;
                            userAppMedia.lengthInSeconds = Number(x.lengthInSeconds);
                            userAppMedia.isSelected = true;
                            userAppMedia.isEnabled = x.isEnabled;
                            userAppMedia.isScheduled = x.isScheduled;
                            userAppMedia.isSelected = true;
                            userAppMedia.url = (x as any).url;
                            userAppMedia.previewPresenterUrl = (x as any).previewPresenterUrl;
                            userAppMedia.thumbnailUrl = (x as any).thumbnailUrl;
                            (userAppMedia as any).previewUrl = (x as any).previewUrl;
                             const mediaApp = this.mediaApps.find(row => row['title'] == x.mediaAppTitle);
                            userAppMedia.iconUrl = mediaApp && mediaApp.iconUrl ? mediaApp.iconUrl : this.generateMediaIconUrl(x.mediaAppType);
                            userAppMedia.iconUrlDark = mediaApp && mediaApp.iconUrlDark ? mediaApp.iconUrlDark : addDarkToImageName(userAppMedia.iconUrl);
                            userAppMedia['isPaidAddOn'] = (mediaApp as any)?.isPaidAddOn ?? x['isPaidAddOn'];
                            this.model.unshift(userAppMedia);
                        });

                    }

                    this.selectMedia();
                    this.isModelLoaded = true;
                    this.isLoading = false;
                },
                error: (error: any) => {
                    this.isLoading = false;
                    this.isModelLoaded = true;
                    this.baseService.processErrorResponse(error);
                },
            });
        this.subscriptions.push(requestSubscription);
    }

    getMoreRecords() {
        if (this.isLoading) { return; }
        this.filterModel.limit = 5;
        this.filterModel.offset = this.totalRecords;
        this.loadingMore = true;
        this.isLoading = true;
        this.service.getList(this.groupId, this.filterModel).subscribe({
            next: (response: any) => {
                if (response && response.length > 0) {
                    response.forEach((x) => {
                        if(!this.model.some(y => y.id == x.id)){
                            x.iconUrl = this.generateMediaIconUrl(x.mediaAppType);
                        x.showDefaultIcon = true;
                        if (this.allMediaSelected) {
                            x.isSelected = true;
                        }
                        x.lengthInSeconds = x.lengthInSeconds == 0 ? 10 : x.lengthInSeconds;
                        this.model.push(x);
                        }
                       
                    });
                    this.totalRecords += response.length;

                } else {
                    this.hasMore = false;
                }
                this.selectMedia();
                this.isLoading = false;
                this.loadingMore = false;
                this.isModelLoaded = true;
            },
            error: (error: any) => {
                this.isLoading = false;
                this.isModelLoaded = true;
                this.loadingMore = false;
                this.baseService.processErrorResponse(error);
            },
        });
    }

    generateMediaIconUrl(mediaAppType: string): string {
        return `./assets/images/media-types/icons/v2/${mediaAppType}.svg`;
    }

    private static readonly PLACEHOLDER = '/assets/images/rp_placeholder.png';

    onThumbnailError(event: Event): void {
        (event.target as HTMLImageElement).src = MediaListMultiSelectComponent.PLACEHOLDER;
    }

    onLightIconError(event: Event): void {
        (event.target as HTMLImageElement).src = MediaListMultiSelectComponent.PLACEHOLDER;
    }

    // Some brand icons (Google, YouTube, ...) have no dark variant by design — the
    // dark-swapped src 404s. Fall back to the light icon first, then the generic
    // placeholder if that fails too.
    onDarkIconError(event: Event, item: UserMediaAppSummaryModel): void {
        const img = event.target as HTMLImageElement;
        if (item.iconUrl && !img.src.endsWith(item.iconUrl)) {
            img.src = item.iconUrl;
        } else {
            img.src = MediaListMultiSelectComponent.PLACEHOLDER;
        }
    }

    selectAllMedia(event: any) {
        if (event) {
            this.model.forEach(x => {
                x.isSelected = true;
                this.isMediaSelected = true;
            });
        }
        else {
            this.model.forEach(x => {
                x.isSelected = false;
                this.isMediaSelected = false;
            });
        }
    }

    toggleMedia(item: UserMediaAppSummaryModel) {
        if (this.model.every(x => x.isSelected)) {
            this.allMediaSelected = true;
        }
        if (!this.model.every(x => x.isSelected)) {
            this.allMediaSelected = false;
        }

        if (this.model.some(x => x.isSelected)) {
            this.isMediaSelected = true;
        }
        else {
            this.isMediaSelected = false;
        }

        if (item.isSelected) {
            this.selectedMediaList.push(item);
        } else {
            this.selectedMediaList.splice(this.selectedMediaList.indexOf(item), 1);
        }
        if (this.singleSelection && item.isSelected) {
            this.model.forEach(x => {
                if (x.id !== item.id) {
                    x.isSelected = false;
                }
            });
            // Clear the selected media list and add only the current item
            this.selectedMediaList = [item];
        } else if (this.singleSelection && !item.isSelected) {
           this.isMediaSelected = false;
           this.model.forEach(x => {
                    x.isSelected = false;
            });
            // If the item is deselected in single selection mode, clear the selected media list
            this.selectedMediaList = [];
        }
    }

    submit() { }

    cancel(): void {
        this.closeDrawer.emit({ selectedMedia: new Array<UserMediaAppSummaryModel>(), updateValue: false });
    }

    onSearch() {
        // Explicit search (Enter key or search icon click) — fires immediately
        this.getMedia();
    }

    clearSearch() {
        this.filterModel.filterKey = '';
        this.getMedia();
    }

    filterKeyChanged() {
        // Push every keystroke through the debounced subject (300 ms)
        this.searchInput$.next(this.filterModel.filterKey);
    }

    sortMedia() {
        if (this.sortOrder === 'ascending') {
            this.filterModel.sortBy = 'Title';
            this.filterModel.sortDir = 'asc';
        } else if (this.sortOrder === 'descending') {
            this.filterModel.sortBy = 'Title';
            this.filterModel.sortDir = 'desc';
        }

        this.getMedia(false, this.totalRecords);
    }

    sendMedia(): void {
        let mediaIdsToRemove = new Array<number>();
        this.selectedMediaList.forEach((x => {
            let media = this.model.filter(y => y.id == x.id);
            if (media && media.length > 0) {
                if (!media[0].isSelected) {
                    mediaIdsToRemove.push(media[0].id);
                }
            }
        }));

        this.selectedMediaList = this.selectedMediaList.filter(x => !mediaIdsToRemove.includes(x.id));

        var selectedMedia = this.model.filter(x => x.isSelected);
        selectedMedia.forEach((x => {
            let exists = this.selectedMediaList.some(y => y.id == x.id);
            if (!exists) {
                this.selectedMediaList.push(x);
            }
        }));

        this.closeDrawer.emit({ selectedMedia: this.selectedMediaList, updateValue: true });

    }

    selectMedia() {
        if (this.selectedMediaList != null && this.selectedMediaList.length > 0) {
            this.model.forEach(x => {
                const media = this.selectedMediaList.find(y => y.id == x.id);
                if (media) {
                    x.isSelected = true;
                    x.lengthInSeconds = media.lengthInSeconds == 0 ? 10 : toNumber(media.lengthInSeconds);
                    x.status = media.status;
                    x.isEnabled = media.isEnabled;
                    x.isActive = media.isActive;
                    x.isScheduled = media.isScheduled;
                    this.isMediaSelected = true;
                }
            });

        }

        this.newMedia.forEach(x => {
            const mediaExists = this.selectedMediaList.some(y => x.id == y.id);
            if(!mediaExists) {
                const existingMedia = this.model.find(y => y.id == x.id);
                this.selectedMediaList.push(existingMedia);
                this.isMediaSelected = true;
            }
        });

        if (this.model.length > 0 && this.model.every(x => x.isSelected)) {
            this.allMediaSelected = true;
        } else {
            this.allMediaSelected = false;
        }
    }

    addMediasToPlaylist() {
        this.isLoading = true;
        let selectedMedia = this.model.filter(x => x.isSelected);
        let playlist = new UserMediaAppEditModel();
        playlist.id = this.selectedPlaylistId;
        playlist.userMediaApps = new Array<DisplayListUserMediaAppModel>();

        selectedMedia.forEach((x, index) => {
            let userMediaApp = new DisplayListUserMediaAppModel();
            userMediaApp.userMediaAppId = x.id;
            userMediaApp.lengthInSeconds = 10;
            userMediaApp.displayOrder = index + 1;

            playlist.userMediaApps.push(userMediaApp);
        });
        this.displayListService.editUserMedia(playlist)
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.closeDrawer.emit({ selectedMedia: selectedMedia, updateValue: true });
                },
                error: (error: any) => {
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    openMediaStoreDrawer() {
        this.isDrawerVisible = true;
        this.showMediaDrawer = true;
        setTimeout(() => {

            this.drawer.open();
        }, 1);
    }

    closeMediaStoreDrawer(event: any) {
        if (event.mediaAdded) {
            this.newMediaId = event.mediaId;
            this.newMedia = event.media;
            this.newMediaIds = event.media.map(x => x.id);

            console.log("event - new media", this.newMedia);
            console.log("event - new media ids", this.newMediaIds);
        }
        this.drawer.close();
        this.isDrawerVisible = false;
        this.showMediaDrawer = false;
        this.getMedia(true);
    }

    close() {
        this.isDrawerVisible = false;
    }

    previewMediaInModal(item: UserMediaAppSummaryModel): void {
        if (!item?.id) {
            this.baseService.error('Preview is not available for this media.');
            return;
        }
        this.modal.create({
            nzContent: DisplayLivePreviewComponent,
            nzData: { userMediaAppId: item.id },
            nzWidth: '80vw',
            nzFooter: null
        });
    }

    ngOnDestroy() {
        this.isLoading = false;
        this.subscriptions.forEach(s => { s.unsubscribe(); });
        this.searchInput$.complete();
    }
}
